import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch } from "react-icons/fa";
import Select from "react-select";
import { v4 as uuidv4 } from "uuid";

import { useUIStateStore } from "@/store/uiStateStore";

import useGetColours from "../helpers/getColours";
import RebrandButtonLink from "./RebrandButtonLink";

function MultipleSelect(props) {
  const { highlightColour2 } = useGetColours();

  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { t } = useTranslation();

  const {
    value,
    onChange,
    valueKey,
    placeholder,
    required,
    name,
    error,
    showLabel = true,
    className,
    options,
    customLabel,
    onInputChange,
  } = props;

  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const customStyles = {
    control: provided => ({
      ...provided,
      "border": "none",
      "boxShadow": "none",
      "borderRadius": "0.5rem",
      "cursor": "pointer",
      "fontSize": "14px",
      "width": "100%",
      "minWidth": "100%",
      "&:hover": { borderColor: "none" },
    }),
    menu: provided => ({
      ...provided,
      backgroundColor: "white",
      width: "100%",
      minWidth: "100%",
    }),
    container: provided => ({
      ...provided,
      width: "100%",
      minWidth: "100%",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#d9d9d9" : "white",
      cursor: "pointer",
    }),
  };

  const customComponents = {
    DropdownIndicator: null,
    IndicatorSeparator: null,
  };

  const [items, setItems] = useState([]);

  useEffect(() => {
    // Use a functional update to avoid needing 'items' in the dependency array
    setItems((currentInternalItems) => {
      // Skip the effect if value is not provided or not an array
      if (!value || !Array.isArray(value)) {
        return currentInternalItems.length > 0 ? [] : currentInternalItems;
      }

      const nextInternalItems = value.map((valFromProp) => {
        // Try to find this item in the currentInternalItems to reuse its ID
        const existingItem = currentInternalItems.find(
          internalItem => internalItem[valueKey] === valFromProp[valueKey],
        );

        return {
          ...valFromProp,
          // Prioritize existing internal ID, then ID from prop, then generate new
          id: existingItem ? existingItem.id : (valFromProp.id || uuidv4()),
          label:
            valFromProp.label
            || options.find(option => option.value === valFromProp[valueKey])
              ?.label,
        };
      });

      // Compare signatures to avoid unnecessary updates
      const currentItemsSignature = JSON.stringify(
        currentInternalItems.map(ci => ({
          key: ci[valueKey],
          label: ci.label,
          id: ci.id,
        })),
      );

      const nextItemsSignature = JSON.stringify(
        nextInternalItems.map(ni => ({
          key: ni[valueKey],
          label: ni.label,
          id: ni.id,
        })),
      );

      // Only update if content has changed
      return currentItemsSignature !== nextItemsSignature ? nextInternalItems : currentInternalItems;
    });
  }, [value, options, valueKey]); // Now we can safely exclude 'items' from the dependency array

  const updateOnChange = (newItems) => {
    // First create a clean copy without internal IDs for the parent component
    const copy = JSON.parse(JSON.stringify(newItems));
    copy.forEach((item) => {
      delete item.id;
    });

    // Only update internal state if the items have actually changed
    const currentItemsString = JSON.stringify(items.map(item => ({ ...item, id: undefined })));
    const newItemsString = JSON.stringify(newItems.map(item => ({ ...item, id: undefined })));

    if (currentItemsString !== newItemsString) {
      // Update local state first
      setItems(newItems);

      // Then notify parent component of the change
      onChange(copy);
    }
  };

  const onChangeItem = (selectedOption) => {
    if (selectedOption) {
      const newItems = [
        ...items,
        {
          id: uuidv4(),
          [valueKey]: selectedOption.value,
          label: selectedOption.label,
        },
      ];
      updateOnChange(newItems);
    }
  };

  const remove = (id) => {
    const newItems = items.filter(n => n.id !== id);
    updateOnChange(newItems);
  };

  return (
    <div
      className={`relative w-full cursor-pointer ${className} ${
        required ? "required-star" : ""
      } ${required && isTunecore ? "text-white" : ""}`}
    >
      {showLabel && (
        <label
          className={`pointer-events-none flex text-sm items-center pb-0.5 ${
            isTunecore
              ? "text-white opacity-70"
              : "font-sentric-alt font-semibold text-sentric-rebrand-grey-text"
          }`}
          htmlFor={name}
        >
          {customLabel || placeholder}
        </label>
      )}

      <div className="flex flex-row items-center gap-2">
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            borderColor: isHovered || isFocused ? highlightColour2 : undefined,
            boxShadow: isHovered || isFocused ? `0 0 0 2px ${highlightColour2}` : undefined,
          }}
          className="w-full rounded-lg"
        >
          <Select
            required
            id={`btn${name}`}
            name={name}
            placeholder={null}
            value={null}
            onChange={onChangeItem}
            options={options.filter(
              o => !items.map(j => j[valueKey]).includes(o.value),
            )}
            noOptionsMessage={() => "Type to search..."}
            onInputChange={onInputChange}
            isSearchable
            styles={customStyles}
            components={customComponents}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={`select-search cursor-pointer font-sentric-alt ${
              isTunecore ? "text-black" : "font-sentric-alt font-semibold"
            }`}
          />
        </div>

        <div
          className="cursor-default"
          style={{ position: "relative", display: "inline-block" }}
        >
          <FaSearch
            className="text-sentric-rebrand-grey-text text-md"
            style={{
              transition: "transform 0.15s ease-out, opacity 0.15s ease-out",
              transform: isFocused ? "scale(1.1)" : "scale(1)",
              opacity: isFocused ? 1 : 0.7,
            }}
          />
        </div>
      </div>

      <div className="flex flex-col items-center gap-2 mt-4">
        {items.map(v => (
          <div
            className={`${isTunecore ? "bg-tunecore-grey-element" : "bg-white"} rounded-lg self-start w-full font-sentric-alt flex items-center justify-between p-2 border`}
            key={`item${v.id}`}
          >
            <span className="font-semibold">{v.label}</span>
            <RebrandButtonLink
              onClick={() => remove(v.id)}
              className="items-center h-4 underline"
            >
              <span className="ml-2">{t("common:remove")}</span>
            </RebrandButtonLink>
          </div>
        ))}
      </div>

      {!!error && (
        <p className="inline-block w-4/5 p-1 pl-3 pr-3 mt-2 text-sm text-white bg-red-700 border border-white rounded-lg">
          {error}
        </p>
      )}
    </div>
  );
}

export default MultipleSelect;
